import { LegalLayout } from '../components/legal/LegalLayout';

/**
 * BestAcco Terms of Use — original draft content. Comprehensive coverage of the
 * standard topics a hotel-discovery/booking platform needs, written for
 * BestAcco's actual model (independent properties listed for browse + booking,
 * deposit or pay-at-property). US / Kentucky governing law. Pending review by
 * counsel.
 */
export default function TermsPage() {
  return (
    <LegalLayout title="Terms of Use" lastUpdated="June 29, 2026">
      <p>
        Welcome to BestAcco. These Terms of Use (the “Terms”) are a binding
        agreement between you and <strong>BestAcco LLC</strong>, a Kentucky,
        U.S.A. limited liability company (“BestAcco,” “we,” “us,” or “our”), and
        govern your access to and use of the BestAcco website at{' '}
        <a href="https://bestacco.com">bestacco.com</a>, together with all pages,
        features, tools, and services we make available there (collectively, the
        “Site”). Please read these Terms carefully before using the Site.
      </p>
      <p>
        By accessing or using the Site — whether by browsing, creating an
        account, or making a booking — you confirm that you have read,
        understood, and agree to be bound by these Terms and by our{' '}
        <a href="/privacy">Privacy Policy</a>, which is incorporated by
        reference. If you do not agree, you may not use the Site. If you are using
        the Site on behalf of a company or other organization, you represent that
        you are authorized to bind that organization to these Terms.
      </p>

      <h2>1. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. When we do, we will revise
        the “Last updated” date above, and for material changes we will provide a
        more prominent notice (such as by email or an on-site banner). Your
        continued use of the Site after changes take effect means you accept the
        updated Terms. If you do not agree to a change, your remedy is to stop
        using the Site.
      </p>

      <h2>2. Changes to the Site</h2>
      <p>
        We are continually improving the Site and may add, modify, restrict
        access to, suspend, or discontinue any part of it at any time, with or
        without notice. We are not liable to you or any third party for any such
        change. These Terms apply to all updated and future versions of the Site.
      </p>

      <h2>3. Who we are and what we do</h2>
      <p>
        BestAcco operates an online platform that helps guests discover and book
        stays at independently owned and operated hotels and properties (each, a
        “Property”). Each Property sets its own rooms, rates, availability, and
        booking conditions, and is solely responsible for the accommodation and
        services it provides. BestAcco facilitates the booking; we are not the
        owner or operator of the Properties and do not provide lodging ourselves.
        A reference to a Property on the Site is not an endorsement or guarantee
        of that Property.
      </p>

      <h2>4. Eligibility and guest accounts</h2>
      <p>
        You must be at least 18 years old, or the age of majority where you live,
        to make a booking or create an account. You may browse the Site without
        an account, but certain features require one. You agree to provide
        accurate, current, and complete information and to keep it up to date. You
        are responsible for safeguarding your account credentials, for all
        activity that occurs under your account, and for promptly notifying us of
        any unauthorized use. We may suspend or terminate accounts that contain
        false information or are used in breach of these Terms.
      </p>

      <h2>5. Intellectual property and your license to use the Site</h2>
      <p>
        The Site and its contents — including text, graphics, logos, page layouts,
        photographs, data compilations, and software (collectively, the
        “Content”) — are owned by BestAcco or our licensors and are protected by
        copyright, trademark, and other laws. We grant you a limited,
        non-exclusive, non-transferable, revocable license to access and use the
        Site for your own personal, non-commercial use. You may not copy,
        reproduce, republish, distribute, modify, create derivative works from,
        publicly display, frame, or commercially exploit any Content, or
        reverse-engineer or attempt to extract the source code of the Site,
        except as expressly permitted by these Terms or applicable law. The
        “BestAcco” name and logo are trademarks of BestAcco LLC; these Terms grant
        you no right to use them.
      </p>

      <h2>6. Acceptable use</h2>
      <p>You agree that you will not:</p>
      <ul>
        <li>make speculative, false, fraudulent, or duplicate bookings;</li>
        <li>
          resell, assign, broker, or advertise bookings, confirmations, or
          inventory on any third-party site or marketplace;
        </li>
        <li>
          use any robot, spider, scraper, or other automated means to access,
          monitor, copy, or harvest the Site, its Content, or rate, availability,
          or inventory data, or use any such data to develop, train, test, or
          benchmark an automated or AI/ML system, in each case without our prior
          written consent (we permit standard search-engine indexing);
        </li>
        <li>
          interfere with, disrupt, overload, or impair the Site or the
          infrastructure that supports it, including by introducing viruses or
          malicious code;
        </li>
        <li>
          probe, scan, or test the vulnerability of, or attempt to bypass,
          circumvent, or defeat, any security, authentication, rate-limiting, or
          access-control measure;
        </li>
        <li>
          access any account, data, or area of the Site you are not authorized to
          access, or impersonate any person or misrepresent your affiliation;
        </li>
        <li>
          post or transmit content that is unlawful, infringing, defamatory,
          obscene, harassing, discriminatory, or that violates the rights or
          privacy of others; or
        </li>
        <li>
          use the Site in violation of any applicable law, regulation, or these
          Terms.
        </li>
      </ul>
      <p>
        We may, but are not obligated to, monitor use of the Site and may remove
        content or suspend access where we believe these Terms have been
        breached. We may disclose information about you and your use of the Site
        where reasonably necessary to enforce these Terms, comply with law, or
        respond to lawful requests.
      </p>

      <h2>7. Reviews and other submissions</h2>
      <p>
        If you submit a review, rating, comment, photo, or other content
        (“Submission”), you remain responsible for it and represent that you have
        the right to share it and that it is accurate and not unlawful or
        infringing. You grant BestAcco a non-exclusive, royalty-free, worldwide,
        sublicensable license to use, reproduce, adapt, publish, and display your
        Submission in connection with the Site and our services. We are not
        obligated to publish or retain any Submission and may remove Submissions
        at our discretion. Nothing here restricts your right to publish a genuine
        review of your experience as protected by applicable law.
      </p>

      <h2>8. Bookings and reservations</h2>
      <h3>8.1 Making a booking</h3>
      <p>
        When you submit a booking request, you are making an offer to reserve the
        selected stay under the rate and conditions presented at checkout. To
        process a booking, you may need to provide your name, contact details,
        and a valid payment method and billing information (“Payment
        Information”). You represent that you are authorized to use any payment
        method you provide and agree to keep your Payment Information accurate and
        current.
      </p>
      <h3>8.2 Rates and when you are charged</h3>
      <p>
        A “rate” is the price for a stay for the dates and occupancy selected, and
        each rate carries its own payment, deposit, cancellation, and refund
        conditions (“Rate Terms”), which are shown before you confirm. Depending
        on the rate, payment may be collected as a deposit at the time of
        booking, charged in full at booking, or due at the Property. By confirming
        a booking, you authorize the applicable charge to your payment method, and
        you agree to pay all charges incurred, including taxes and any mandatory
        fees disclosed at checkout. Payments are handled by third-party payment
        processors; we do not store full card numbers ourselves.
      </p>
      <h3>8.3 Confirmation; our right to decline or cancel</h3>
      <p>
        A booking is accepted only when you receive a confirmation (on screen or
        by email) that includes a booking reference. We or the Property may
        decline, cancel, or modify a booking — for example, due to suspected
        fraud or abuse, payment failure or chargeback, pricing or availability
        errors, Property closure, force majeure, or to comply with law. If we
        cancel a booking for such reasons, our liability to you will not exceed
        the amount you paid for the affected booking.
      </p>
      <h3>8.4 Cancellations, changes, and no-shows</h3>
      <p>
        Each rate’s cancellation, modification, and no-show terms are shown before
        you confirm and govern your booking. Some rates are non-cancellable or
        non-refundable. Where a booking is cancelled outside the
        free-cancellation window, is non-cancellable, or results in a no-show,
        charges may be retained in accordance with the applicable Rate Terms.
        Please review them carefully before booking.
      </p>
      <h3>8.5 Prohibited bookings</h3>
      <p>
        You agree that your bookings are made in good faith for your own stay or
        that of your invited guests, and not to resell, hold inventory, or
        speculate on demand. We may cancel any booking we reasonably believe is a
        prohibited booking.
      </p>
      <h3>8.6 Errors</h3>
      <p>
        Despite our efforts, the Site may contain errors in Property details,
        availability, rates, or descriptions, and processing errors can occur. We
        may correct any such error and cancel or modify any affected booking, and
        in that event our only liability to you is a refund of amounts you paid
        for the affected booking.
      </p>
      <h3>8.7 Check-in requirements</h3>
      <p>
        At check-in, the guest of record must meet the Property’s minimum age
        requirement and present valid, government-issued photo identification
        matching the booking, together with a valid payment method. The Property
        may refuse accommodation if these requirements are not met, in which case
        the applicable cancellation, no-show, and early-departure terms apply.
      </p>
      <h3>8.8 Currency and fees</h3>
      <p>
        Rates are displayed in the currency indicated at checkout, which may
        differ from your local currency. Any currency conversion shown is for
        convenience only and may differ from the rate applied when you are
        charged. Your card issuer may apply its own exchange rates or
        cross-border fees, for which we are not responsible.
      </p>
      <h3>8.9 Property images and amenities</h3>
      <p>
        Photographs and descriptions of Properties and amenities are provided for
        general illustration and may not reflect the exact room, layout, view, or
        amenities available for your stay. Amenities may be seasonal, fee-based,
        limited, or temporarily unavailable. We make no warranty that any image or
        amenity listing is current or complete; please contact the Property to
        confirm anything important to you.
      </p>

      <h2>9. Independent properties and third-party links</h2>
      <p>
        Properties are independent businesses responsible for fulfilling your
        booking and for the conduct of their staff and the condition of their
        premises. Any dispute regarding your stay is between you and the Property.
        The Site may also contain links to third-party websites and services that
        we do not control; we are not responsible for their content, products,
        policies, or security, and your use of them is at your own risk.
      </p>

      <h2>10. Communications</h2>
      <p>
        By providing your email address or phone number, you agree that we and our
        service providers may contact you about your bookings, account, and
        support requests, including by email, SMS, or phone. Transaction-related
        messages (such as booking confirmations and updates) are not marketing
        communications. You can opt out of marketing emails using the unsubscribe
        link; standard message and data rates may apply to text messages.
      </p>

      <h2>11. Disclaimers</h2>
      <p>
        To the fullest extent permitted by law, the Site is provided on an “as is”
        and “as available” basis, and BestAcco disclaims all warranties, express
        or implied, including merchantability, fitness for a particular purpose,
        title, and non-infringement. We do not warrant that the Site will be
        uninterrupted, secure, or error-free, that defects will be corrected, or
        that information on the Site (including rates, availability, and Property
        details) is accurate, complete, or current. We make no warranty regarding
        the conduct of any Property or third party or the quality of their
        services.
      </p>

      <h2>12. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, BestAcco and its affiliates,
        officers, employees, and agents will not be liable for any indirect,
        incidental, special, consequential, exemplary, or punitive damages, or for
        lost profits, revenue, goodwill, or data, arising out of or relating to
        your use of the Site or any booking, even if advised of the possibility of
        such damages. Our total aggregate liability for any claim relating to the
        Site or a booking will not exceed the greater of the amount you paid to
        BestAcco for the booking giving rise to the claim or USD $100. Some
        jurisdictions do not allow certain limitations, so some of the above may
        not apply to you.
      </p>

      <h2>13. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless BestAcco and its
        affiliates, officers, employees, and agents from and against any claims,
        liabilities, damages, losses, and expenses (including reasonable
        attorneys’ fees) arising out of or related to your use of the Site, your
        bookings, your Submissions, or your breach of these Terms or of any law or
        third-party right.
      </p>

      <h2>14. Governing law and dispute resolution</h2>
      <p>
        These Terms and any dispute arising out of or relating to them or the Site
        are governed by the laws of the State of Kentucky, United States, without
        regard to its conflict-of-laws rules. Before bringing any formal claim,
        you agree to first contact us using the details below and give us a
        reasonable opportunity to resolve the matter informally. Any dispute that
        is not resolved informally will be brought exclusively in the state or
        federal courts located in Kentucky, and you consent to the personal
        jurisdiction and venue of those courts. Each party will bear its own costs
        except as a court may otherwise order.
      </p>

      <h2>15. Termination</h2>
      <p>
        These Terms remain in effect while you use the Site. We may suspend or
        terminate your access at any time if we believe you have breached these
        Terms, if we discontinue the Site, or as necessary to comply with law. You
        may stop using the Site at any time and, where available, delete your
        account. Provisions that by their nature should survive termination —
        including those on intellectual property, disclaimers, limitation of
        liability, indemnification, governing law, and these miscellaneous terms —
        will survive.
      </p>

      <h2>16. Copyright complaints</h2>
      <p>
        We respect intellectual-property rights. If you believe content on the
        Site infringes a copyright you own or are authorized to enforce, please
        send a written notice with enough detail to identify the work and the
        allegedly infringing material, your contact information, a good-faith
        statement, and your signature, to the contact in Section 18. We will
        respond to properly submitted notices as required by applicable law and
        may remove infringing material and terminate repeat infringers.
      </p>

      <h2>17. Miscellaneous</h2>
      <p>
        These Terms (together with the Privacy Policy and any rate-specific terms)
        are the entire agreement between you and BestAcco regarding the Site and
        supersede any prior agreements on that subject. You may not assign these
        Terms without our consent; we may assign them in connection with a merger,
        acquisition, or sale of assets. If any provision is held unenforceable,
        the remaining provisions stay in effect. Our failure to enforce a
        provision is not a waiver of it. Nothing in these Terms creates a
        partnership, agency, or employment relationship between you and BestAcco.
        Section headings are for convenience only.
      </p>

      <h2>18. Contact us</h2>
      <p>Questions, notices, or concerns about these Terms? Contact us at:</p>
      <p>
        BestAcco LLC<br />
        Attn: Prateek Juneja<br />
        99 Free Henry Ford Road, Madisonville, KY 42431, USA<br />
        <a href="mailto:prateek.juneja@bestacco.com">prateek.juneja@bestacco.com</a>
      </p>
    </LegalLayout>
  );
}
